import { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.js';
import { getInitialen } from '../shared/utils/formatters.js';
import { useModuleLabels } from '../shared/hooks/useModuleLabels.js';
import { useOfflineSync } from '../shared/hooks/useOfflineSync.js';
import { NotificatieBell } from '../../modules/notificaties/components/NotificatieBell.js';
import { GlobalSearch } from '../shared/components/GlobalSearch.js';
import { useBrand } from '../shared/components/BrandContext.js';
import { KIcon } from '../shared/components/KIcon.js';
import { MerkLogo } from './MerkLogo.js';
import { gesprekkenLabel } from './navigatie.js';

/**
 * Lichte contentkop (Kordaro v4) — vervangt de bordeaux balk over volle breedte.
 * Desktop: moduletitel + vestiging links, zoeken/sync/meldingen/gebruiker rechts.
 * <1024: menuknop (inklapbare zijbalk) — op gsm (<768) staat het logo hier en is de voet de navigatie.
 */
export function Topbar({ onMenu }: { onMenu?: () => void }) {
  const { user, activeEntity, accessibleEntities, switchEntity, logout } = useAuth();
  const brand = useBrand();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const labels = useModuleLabels();
  const { isOnline, pendingCount, isSyncing, triggerSync } = useOfflineSync();

  // Moduletitel uit de route — gebruikt de instelbare modulenamen
  const pageTitle = useMemo(() => {
    const p = location.pathname;
    if (p.startsWith('/dashboard')) return 'Dashboard';
    if (p.startsWith('/winkel')) return labels.winkel.label;
    if (p.startsWith('/dossiers')) return labels.buitendienst.label;
    if (p.startsWith('/crm')) return labels.crm.label;
    if (p.startsWith('/logistiek')) return labels.logistiek.label;
    if (p.startsWith('/communicatie')) return gesprekkenLabel(labels.communicatie.label);
    if (p.startsWith('/planning')) return labels.planning.label;
    if (p.startsWith('/settings')) return 'Instellingen';
    if (p.startsWith('/notificaties')) return 'Notificaties';
    if (p.startsWith('/buitendienst')) return labels.buitendienst.label;
    if (p.startsWith('/vorderingsstaten')) return 'Vorderingsstaat';
    if (p.startsWith('/projecten')) return 'Projecten';
    if (p.startsWith('/service')) return 'Service';
    if (p.startsWith('/marketing')) return 'Marketing';
    if (p.startsWith('/catalogus')) return 'Catalogus';
    return brand.naam;
  }, [location.pathname, labels, brand.naam]);

  const eigenTitel = location.pathname === '/communicatie' || location.pathname === '/communicatie/';

  // Cmd+K shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(o => !o);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Gebruikersmenu sluiten bij klik buiten of Escape
  useEffect(() => {
    if (!userMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setUserMenuOpen(false);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setUserMenuOpen(false); };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', handler); document.removeEventListener('keydown', esc); };
  }, [userMenuOpen]);

  const hasMultipleEntities = accessibleEntities.length > 1;

  // Demo banner: dagen resterend uit demoExpiresAt
  const [demoBannerHidden, setDemoBannerHidden] = useState(() =>
    localStorage.getItem('demoBannerHidden') === 'true'
  );
  const demoInfo = useMemo(() => {
    if (!activeEntity?.isDemo) return null;
    const expiresAt = activeEntity.demoExpiresAt ? new Date(activeEntity.demoExpiresAt) : null;
    if (!expiresAt) return { daysRemaining: null };
    const days = Math.max(0, Math.ceil((expiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
    return { daysRemaining: days };
  }, [activeEntity]);

  const handleSwitch = async (entityId: string) => {
    if (entityId === activeEntity?.id) { setUserMenuOpen(false); return; }
    setUserMenuOpen(false);
    try { await switchEntity(entityId); } catch { /* entity switch failed silently */ }
  };

  return (
    <>
      {demoInfo && !demoBannerHidden && (
        <div className="k-verberg-volscherm bg-[#FFF3D6] text-[#785411] text-[13px] leading-5 font-medium flex items-center justify-between px-4 py-2 flex-shrink-0 border-b border-card-border">
          <span>
            Dit is een <strong>demo-omgeving</strong>.
            {demoInfo.daysRemaining !== null && (
              <> Nog <strong>{demoInfo.daysRemaining} dag{demoInfo.daysRemaining !== 1 ? 'en' : ''}</strong> geldig.</>
            )}
          </span>
          <button
            type="button"
            onClick={() => { setDemoBannerHidden(true); localStorage.setItem('demoBannerHidden', 'true'); }}
            className="ml-4 p-2 -m-2"
            aria-label="Demomelding verbergen"
          >
            <KIcon naam="sluit" size={16} />
          </button>
        </div>
      )}

      <header className="k-shell-header k-verberg-volscherm flex items-center justify-between gap-3 px-4 md:px-6 lg:px-8 flex-shrink-0 z-30">
        <div className="flex items-center gap-3 min-w-0">
          {onMenu && (
            <button type="button" onClick={onMenu} className="k-button k-button--icon hidden md:inline-flex lg:hidden" aria-label="Menu openen">
              <KIcon naam="menu" />
            </button>
          )}
          {/* Logo: op gsm/tablet hier, op desktop in de zijbalk */}
          <span className="lg:hidden flex-none"><MerkLogo breedte={140} /></span>
          <div className="hidden lg:block min-w-0">
            {/* V08: Gesprekken heeft zijn eigen titel in de inbox → hier geen tweede "Gesprekken" */}
            {!eigenTitel && <p className="text-xl leading-7 font-semibold text-gray-900 truncate">{pageTitle}</p>}
            {activeEntity && <p className="k-meta truncate">{activeEntity.name}</p>}
          </div>
        </div>

        <div className="flex items-center gap-1 md:gap-2 flex-none">
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            className="k-button k-button--text !text-gray-700 min-w-[44px]"
            aria-label="Zoeken (Ctrl+K)"
          >
            <KIcon naam="zoek" />
            <span className="hidden xl:inline">Zoeken</span>
          </button>

          {!isOnline && (
            <span className="k-status k-status--warning" role="status">
              <KIcon naam="offline" />
              <span>Offline{pendingCount > 0 ? ` · ${pendingCount} wacht` : ''}</span>
            </span>
          )}
          {isOnline && pendingCount > 0 && (
            <button
              type="button"
              onClick={triggerSync}
              className="k-status k-status--info min-h-[44px]"
              aria-busy={isSyncing}
            >
              <KIcon naam="sync" className={isSyncing ? 'animate-spin' : undefined} />
              <span>{isSyncing ? 'Bezig…' : `${pendingCount} wijziging${pendingCount !== 1 ? 'en' : ''} synchroniseren`}</span>
            </button>
          )}

          <NotificatieBell />

          <div ref={menuRef} className="relative">
            <button
              type="button"
              onClick={() => setUserMenuOpen(o => !o)}
              aria-haspopup="menu"
              aria-expanded={userMenuOpen}
              aria-label={user ? `Account van ${user.firstName} ${user.lastName}` : 'Account'}
              className="flex items-center gap-2 min-h-[44px] min-w-[44px] justify-center rounded-lg px-1 hover:bg-white"
            >
              {user && (
                <span className="w-9 h-9 rounded-full bg-brand-light text-brand flex items-center justify-center text-[13px] font-semibold">
                  {getInitialen(user.firstName, user.lastName)}
                </span>
              )}
              <span className="hidden xl:block text-left text-sm leading-5 font-medium text-gray-900">
                {user?.firstName} {user?.lastName}
              </span>
            </button>

            {userMenuOpen && (
              <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-xl shadow-lg border border-card-border z-50 overflow-hidden" role="menu">
                {user && (
                  <div className="px-4 py-3 border-b border-card-border">
                    <p className="text-sm font-semibold text-gray-900">{user.firstName} {user.lastName}</p>
                    <p className="k-meta">{user.email}</p>
                  </div>
                )}
                <div className="border-b border-card-border py-1">
                  <p className="k-meta px-4 pt-2 pb-1">Winkel</p>
                  {accessibleEntities.map(entity => (
                    <button
                      key={entity.id}
                      type="button"
                      role="menuitemradio"
                      aria-checked={entity.id === activeEntity?.id}
                      onClick={() => hasMultipleEntities ? handleSwitch(entity.id) : undefined}
                      className={`w-full min-h-[44px] flex items-center justify-between px-4 py-2 text-left ${hasMultipleEntities ? 'hover:bg-gray-50 cursor-pointer' : ''}`}
                    >
                      <span className="flex-1 min-w-0">
                        <span className="text-sm text-gray-900 truncate block">{entity.name}</span>
                        {entity.dataScope === 'sales_only' && <span className="k-status k-status--warning mt-1">Enkel verkoop</span>}
                      </span>
                      {entity.id === activeEntity?.id && <KIcon naam="vink" className="text-brand ml-2" />}
                    </button>
                  ))}
                </div>
                <div className="py-1">
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => { navigate('/settings'); setUserMenuOpen(false); }}
                    className="w-full min-h-[44px] flex items-center gap-3 px-4 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <KIcon naam="persoon" /> Profiel
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => { setUserMenuOpen(false); logout(); }}
                    className="w-full min-h-[44px] flex items-center gap-3 px-4 text-sm text-status-red hover:bg-[#FBEDEF]"
                  >
                    <KIcon naam="uitloggen" /> Uitloggen
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
